/**
 * Calendar Widget Component
 * Handles filter interactions, AJAX updates, and link-through URL building
 */

import {
    setupFilterDropdowns,
    setupCustomDatePicker,
    getActiveFilters,
    renderChips,
    updateCounter,
    buildFilterParams,
    createEventCardHTML,
    populateFiltersFromURL,
    getFilterLabelDatePlaceholder
} from './calendarShared.js';

function initCalendarWidget(widget) {
    if (widget.dataset.calendarInitialized) return;
    widget.dataset.calendarInitialized = 'true';

    const ajaxUrl = widget.dataset.ajaxUrl;
    const calendarUrl = widget.dataset.calendarUrl;
    const stiftId = widget.dataset.stiftId;
    const nodeId = widget.dataset.nodeId;

    const checkboxes = widget.querySelectorAll('input[type="checkbox"]');
    const clearButton = widget.querySelector('.calendar-widget__clear-filters');
    const ctaButton = widget.querySelector('.calendar-widget__cta');
    const gridElement = widget.querySelector('.calendar-widget__grid');

    // Initialize filter dropdowns
    setupFilterDropdowns(widget, 'calendar-widget');

    // Initialize custom date picker
    setupCustomDatePicker(widget, 'calendar-widget', onFilterChange);

    // Handle filter changes
    function onFilterChange() {
        updateActiveFilters();
        fetchResults();
        updateCtaUrl();
    }

    checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', onFilterChange);
    });

    // Clear all filters
    if (clearButton) {
        clearButton.addEventListener('click', function() {
            checkboxes.forEach(cb => cb.checked = false);
            const dateInput = widget.querySelector('input[name="fromDate"]');
            if (dateInput) {
                dateInput.value = '';
                const button = widget.querySelector('.calendar-widget__date-picker .calendar-widget__filter-button span');
                if (button) button.textContent = getFilterLabelDatePlaceholder(widget);
            }
            onFilterChange();
        });
    }

    // Update active filter chips display
    function updateActiveFilters() {
        const activeFilters = getActiveFilters(widget);
        renderChips(activeFilters, widget, 'calendar-widget', onFilterChange);
    }

    // Fetch filtered results via AJAX
    function fetchResults() {
        const filters = getActiveFilters(widget);
        const params = buildFilterParams(filters, { nodeId: nodeId });

        // Build URL
        const separator = ajaxUrl.includes('?') ? '&' : '?';
        const url = params.toString() ? `${ajaxUrl}${separator}${params.toString()}` : ajaxUrl;

        console.log('CalendarWidget: Fetching results', {
            ajaxUrl: ajaxUrl,
            nodeId: nodeId,
            filters: filters,
            params: params.toString(),
            finalUrl: url
        });

        fetch(url)
            .then(response => response.json())
            .then(data => {
                console.log('CalendarWidget: Data received', {
                    count: data.count,
                    itemsCount: data.items.length
                });

                // Update count
                updateCounter(data.count, widget, 'calendar-widget');

                // Update grid with new items
                if (gridElement) {
                    if (data.items.length === 0) {
                        gridElement.innerHTML = '<div class="calendar-widget__no-results">Ingen arrangementer matcher dine filtre</div>';
                    } else {
                        const eventDetailBaseUrl = widget.dataset.eventDetailUrl || '';
                        gridElement.innerHTML = data.items.map(item => createEventCardHTML(item, eventDetailBaseUrl)).join('');
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching calendar results:', error);
            });
    }

    // Update the CTA button URL with active filters
    function updateCtaUrl() {
        if (!ctaButton || !calendarUrl) return;

        const filters = getActiveFilters(widget);
        const params = new URLSearchParams();

        filters.targetgroups.forEach(val => params.append('targetgroups[]', val));
        filters.organisers.forEach(val => params.append('organisers[]', val));
        filters.bydel.forEach(val => params.append('bydel[]', val));
        if (filters.fromDate) {
            params.append('fromDate', filters.fromDate);
        }

        const queryString = params.toString();
        ctaButton.href = queryString ? `${calendarUrl}?${queryString}` : calendarUrl;
    }

    // Pre-populate filters from URL params on page load
    populateFiltersFromURL(widget, 'calendar-widget');

    // Always trigger initial AJAX fetch
    updateActiveFilters();
    fetchResults();
    updateCtaUrl();
}

document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-component="CalendarWidget"]').forEach(initCalendarWidget);

    new MutationObserver(function(mutations) {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (node.matches('[data-component="CalendarWidget"]')) {
                    initCalendarWidget(node);
                }
                node.querySelectorAll('[data-component="CalendarWidget"]').forEach(initCalendarWidget);
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
});
